import { useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import Link from '../Atoms/Link';
import Contact from '../Contact'
import ArrowDown from '../../images/icons/arrow.svg';
import ArrowButton from '../Atoms/ArrowButton';
import { SELLING_PRODUCT_FORM_ROUTE } from '../../utils/routes/const';

type EstimationHeroType = {
  watch: any,
  isThirdOpen: boolean,
  setIsThirdOpen: any
}

const EstimationHero = ({ watch, isThirdOpen, setIsThirdOpen }: EstimationHeroType) => {
  const { t } = useTranslation();
  const [isFirstOpen, setIsFirstOpen] = useState(false);
  const [isSecondOpen, setIsSecondOpen] = useState(false);

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
  });

  const format = (value: number) => currencyFormatter.format(value);

  return (
    <section className="py-12 pt-16 mt-24 lg:mt-44 px-7 bg-white">
      <div className="flex flex-col lg:flex-row min-h-screen max-w-screen-2xl w-full mx-auto">
        <div className="lg:w-1/12 xl:block hidden" />
        <div className="lg:w-3/12 lg:mr-32 lg:sticky top-16 self-start">
          <h1 className="font-cirka text-primary text-6xl mb-12">{`${watch.brand || ''} ${watch.model || ''}`}</h1>
          <hr style={{
            backgroundImage: 'linear-gradient(to right, #A38D70 40%, rgba(255, 255, 255, 0) 20%)',
            backgroundSize: '10px 1px'
          }} />
          <div className="mt-9">
            <p className="font-cirka text-4xl">{t('estimationHero.betterValuation')}</p>
            <Link to={SELLING_PRODUCT_FORM_ROUTE(watch.slug)} className="flex items-center my-5 no-animation">
              <ArrowButton isGhost={false} className="w-10 mr-5" />
              {t('estimationHero.beginEstimation')}
            </Link>
          </div>
        </div>
        <div className="lg:w-1/12 xl:block hidden" />
        <div className="lg:w-7/12">
          <p className="font-cirka text-xl mb-8">{t('estimationHero.valueApproximation')}</p>
          <div className="bg-gray mb-8">
            <div className="flex flex-col lg:flex-row p-8">
              <div className="w-2/3">
                <b className="font-roboto">{t('estimationHero.estimatedSellingPrice')}</b>
              </div>
              <div className="w-1/3 text-primary font-cirka text-3xl text-right mt-4 lg:mt-0">
                {watch.marketValueFrom && format(watch.marketValueFrom)}/{watch.marketValueTo && format(watch.marketValueTo)}
              </div>
            </div>
          </div>
          <div className="border border-primary mb-12">
            <div className="flex p-8">
              <div className="w-2/3">
                <p>{t('estimationHero.estimatedIncome')}</p>
              </div>
            </div>
            <hr style={{
              backgroundImage: 'linear-gradient(to right, #A38D70 40%, rgba(255, 255, 255, 0) 20%)',
              backgroundSize: '10px 1px',
              height: '2px',
              borderTopWidth: 0,
              width: 'calc(100% - 64px)',
              margin: '0 auto',
            }} />
            <p className="font-roboto font-light p-8">{t('estimationHero.estimatedIncome.info')}</p>
          </div>
          <details className="px-4 py-2 mt-4 border border-primary" open={isFirstOpen} onToggle={() => setIsFirstOpen(!isFirstOpen)}>
            <summary className='marker:content-none flex flex-row items-center cursor-pointer text-2xl text-primary'>
              <ArrowDown className={`transition-all duration-100 h-6 w-6 mr-6 ${!isFirstOpen ? '-rotate-90' : ''} fill-black`}/>
              <p className="font-cirka my-6">{t('estimationHero.instructions')}</p>
            </summary>
            <p className="font-roboto font-light text-lg mb-4">
              {t('estimationHero.instructions.info.0')}
            </p>
            <p className="font-roboto font-light text-lg">
              {t('estimationHero.instructions.info.1')}
            </p>
            <ul className="list-disc font-roboto font-light text-lg mb-12 pl-5 mt-4">
              <li>{t('estimationHero.salesProcessOverview.0.text')}</li>
              <li>{t('estimationHero.salesProcessOverview.1.text')}</li>
              <li>{t('estimationHero.salesProcessOverview.2.text')}</li>
            </ul>
            <p className="font-roboto font-light text-lg">
              {t('estimationHero.salesProcessOverview.info')}
            </p>
          </details>
          <details className="px-4 py-2 mt-4 border border-primary" open={isSecondOpen} onToggle={() => setIsSecondOpen(!isSecondOpen)}>
            <summary className='marker:content-none flex flex-row items-center cursor-pointer text-2xl text-primary'>
              <ArrowDown className={`transition-all duration-100 h-6 w-6 mr-6 ${!isSecondOpen ? '-rotate-90' : ''} fill-black`}/>
              <p className="font-cirka my-6">{t('estimationHero.valuationBreakdown')}</p>
            </summary>
            <div>
              <div className="flex py-8">
                <div className="w-2/3">
                  <p>{t('estimationHero.estimatedSellingPrice')}</p>
                </div>
                <div className="w-1/3 text-right font-bold text-lg">
                  {format(watch.valuation)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p className="font-roboto font-light pb-5">{t('estimationHero.commissionCD')}</p>
                  <p className="text-sm font-roboto font-light">{t('estimationHero.commissionCD.info')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.watchprojectCost)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p className="font-roboto font-light pb-5">{t('estimationHero.shippingInsurance')}</p>
                  <p className="text-sm font-roboto font-light">{t('estimationHero.shippingInsurance.info')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.shippingCost)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p className="font-roboto font-light pb-5">{t('estimationHero.revision')}</p>
                  <p className="text-sm font-roboto font-light">{t('estimationHero.revision.info')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.revision)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p className="font-roboto font-light pb-5">{t('estimationHero.platformCommission')}</p>
                  <p className="text-sm font-roboto font-light">{t('estimationHero.platformCommission.info')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.chrono24Cost)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p className="font-roboto font-light pb-5">{t('estimationHero.taxes')}</p>
                  <p className="text-sm font-roboto font-light">{t('estimationHero.taxes.info')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.tax)}
                </div>
              </div>
              <hr className="border-primary" />
              <div className="flex py-8">
                <div className="w-2/3">
                  <p>{t('estimationHero.estimatedRetailPrice')}</p>
                </div>
                <div className="w-1/3 text-right font-bold">
                  {format(watch.retailPrice)}
                </div>
              </div>
            </div>
            <hr className="border-primary" />
            <div className="border border-primary">
              <p className="p-8">{t('estimationHero.accuracy')}</p>
            </div>
          </details>
          <Contact url={SELLING_PRODUCT_FORM_ROUTE(watch.slug)} wrapperClass="xl:ml-auto flex flex-col md:flex-row bg-top bg-repeat-x pt-12" />
          <div className='marker:content-none flex flex-row items-center cursor-pointer text-2xl text-primary' onClick={() => setIsThirdOpen(!isThirdOpen)}>
            <ArrowDown className={`transition-all duration-100 h-6 w-6 mr-6 ${!isThirdOpen ? '-rotate-90' : ''} fill-black`}/>
            <p className="font-cirka my-6">{t('aboutUs.textimage.4.cta')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EstimationHero;
